import { getOmnibus } from '@services/omnibus';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

export const execOnLoad = (initFunction, selector) => {
  if (!initFunction || !selector) return;

  if (document.readyState != 'loading') {
    [].forEach.call(document.querySelectorAll(selector), (el) => {
      initFunction(el);
    });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      [].forEach.call(document.querySelectorAll(selector), (el) => {
        initFunction(el);
      });
    });
  }
};

/**
 * Formats a price value according to a specified format template.
 *
 * The function converts the given value into various formatted string representations
 * based on placeholders in the `format` string. It supports multiple formats including
 * comma separators, dot separators, and space separators.
 *
 * @param {Object} params - Parameters object.
 * @param {string} [params.format=window?.dAgency?.strings?.moneyFormat] - A string template for the format.
 *        This template can contain placeholders like `{{amount}}`, `{{amount_no_decimals}}`, etc.
 * @param {number} params.value - The price value to format, provided as an integer in cents.
 *
 * @returns {string|undefined} The formatted price string based on the format template. If `value` or `format` is missing, returns `undefined`.
 */
export const formatPrice = ({ format = window?.dAgency?.strings?.moneyFormat, value }) => {
  if ((!value && value != 0) || !format) return;
  const formattedValue = (parseInt(value) / 100).toFixed(2);
  const placeholders = [
    {
      key: 'amount',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals',
      formatter: () => Math.round(formattedValue),
    },
    {
      key: 'amount_with_comma_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_comma_separator',
      formatter: () => `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    },
    {
      key: 'amount_with_apostrophe_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, "'");
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
    {
      key: 'amount_no_decimals_with_space_separator',
      formatter: () => `${Math.round(formattedValue)}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    },
    {
      key: 'amount_with_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart},${decimalPart}`;
      },
    },
    {
      key: 'amount_with_period_and_space_separator',
      formatter: () => {
        const splittedValue = formattedValue.split('.');
        const integerPart = splittedValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const decimalPart = splittedValue[1] ?? '00';
        return `${integerPart}.${decimalPart}`;
      },
    },
  ];

  return placeholders.reduce(
    (acc, placeholder) =>
      acc.replaceAll(new RegExp(`{{[ ]*${placeholder.key}[ ]*}}`, 'g'), placeholder.formatter(value)),
    format,
  );
};

/**
 * Initializes a number input field with increment and decrement buttons.
 *
 * @param {Object} params - The parameters for initializing the number field.
 * @param {HTMLElement} params.plusBtn - The button element used to increase the value.
 * @param {HTMLElement} params.minusBtn - The button element used to decrease the value.
 * @param {HTMLElement} params.field - The field element where the value is displayed or entered.
 *
 * @throws {Error} Will log an error if `plusBtn`, `minusBtn`, or `field` is missing.
 */
export const initNumberField = ({ plusBtn, minusBtn, field }) => {
  if (!plusBtn || !minusBtn || !field) {
    console.error('Cannot init number field, missing arguments.');
    return;
  }

  const min = field.dataset.min ? parseInt(field.dataset.min, 10) : null;
  const max = field.dataset.max ? parseInt(field.dataset.max, 10) : null;

  const updateButtonStates = () => {
    if (min !== null) {
      if (value <= min) {
        minusBtn.tagName === 'BUTTON' ? (minusBtn.disabled = true) : minusBtn.setAttribute('disabled', '');
      } else {
        minusBtn.tagName === 'BUTTON' ? (minusBtn.disabled = false) : minusBtn.removeAttribute('disabled');
      }
    }
    if (max !== null) {
      if (value >= max) {
        plusBtn.tagName === 'BUTTON' ? (plusBtn.disabled = true) : plusBtn.setAttribute('disabled', '');
      } else {
        plusBtn.tagName === 'BUTTON' ? (plusBtn.disabled = false) : plusBtn.removeAttribute('disabled');
      }
    }
  };

  const updateValue = (newValue) => {
    const clampedValue = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, newValue));
    field.tagName === 'INPUT' ? (field.value = clampedValue) : (field.innerText = clampedValue);
    if (field.tagName === 'INPUT' && value != clampedValue)
      ['input', 'change'].forEach((evt) => field.dispatchEvent(new Event(evt, { bubbles: true })));
    value = clampedValue;
    updateButtonStates();
  };

  let value = parseInt(field.tagName === 'INPUT' ? field.value : field.innerText);
  updateValue(value);

  // Sync internal state when the input value is changed externally (e.g., cart incremental update).
  // Uses a dedicated custom event to avoid triggering the 'change' listener in initLine (which would call changeCart).
  field.addEventListener('cart-qty-sync', () => {
    const externalVal = parseInt(field.tagName === 'INPUT' ? field.value : field.innerText);
    if (!isNaN(externalVal) && externalVal !== value) {
      value = externalVal;
      updateButtonStates();
    }
  });

  plusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value + 1);
  });

  minusBtn.addEventListener('click', (e) => {
    e.preventDefault();
    updateValue(value - 1);
  });
};

/**
 * Retrieves a CSS property value from the given container element.
 *
 * @param {HTMLElement} container - The element to retrieve the CSS property from.
 * @param {string} prop - The name of the CSS property to retrieve.
 *
 * @returns {string} The value of the given CSS property.
 *
 * @throws {Error} Will log an error if `container` or `prop` is missing.
 */
export const getCssProp = (container, prop) => {
  if (!container || !prop) {
    console.error('Cannot get css prop, missing container or prop.');
    return;
  }

  return getComputedStyle(container)[prop];
};

/**
 * Retrieves a CSS variable value from the given container element.
 *
 * @param {HTMLElement} container - The element to retrieve the CSS variable from.
 * @param {string} variable - The name of the CSS variable to retrieve.
 *
 * @returns {string} The value of the given CSS variable.
 *
 * @throws {Error} Will log an error if `container` or `variable` is missing.
 */
export const getCssVariable = (container, variable) => {
  if (!container || !variable) {
    console.error('Cannot get css variable balue, missing container or variable.');
    return;
  }

  return getComputedStyle(container).getPropertyValue(variable);
};

/**
 * Debounces an async function in a way that can be awaited.
 * By default debounce doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to debounce calls for.
 * @param wait same function as lodash.debounce's wait parameter.
 *             Call this function after this amount of time has passed since the last call.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export const asyncDebounce = (func, wait) => {
  const debounced = debounce((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);
  return (...args) =>
    new Promise((resolve, reject) => {
      debounced(resolve, reject, args);
    });
};

/**
 * Throttles an async function in a way that can be awaited.
 * By default throttle doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to throttle calls for.
 * @param wait same function as lodash.throttle's wait parameter.
 *             Call this function at most this often.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export const asyncThrottle = (func, wait) => {
  const throttled = throttle((resolve, reject, args) => {
    func(...args)
      .then(resolve)
      .catch(reject);
  }, wait);
  return (...args) =>
    new Promise((resolve, reject) => {
      throttled(resolve, reject, args);
    });
};

/**
 * A utility function that detects whether the current device is running iOS (iPhone, iPad, or iPod).
 * It also correctly identifies modern iPad devices running iPadOS, which may report their platform as MacIntel.
 */
export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;

  const isIPadPro = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (isIOS || isIPadPro) && !window.MSStream;
};

/**
 * Handles the visibility and update of the Omnibus price.
 * Fetches and displays the Omnibus value when a discount is detected,
 * including discounts added dynamically by third-party apps.
 *
 * @param {HTMLElement} container - DOM element containing the section markup.
 * @param {HTMLElement} priceWrapper - DOM element wrapping the product price, used to detect discounted prices.
 */
export const updateOmnibus = (container, priceWrapper) => {
  if (!container || !priceWrapper) {
    console.error('Cannot update Omnibus, missing container or priceWrapper.');
    return;
  }

  const omnibusContainer = container.querySelector('.omnibus__container[data-sku][data-variant-price]');
  const omnibusValue = container.querySelector('.omnibus__container [data-omnibus-value]');

  if (!omnibusContainer || !omnibusValue) return;

  const variantCompareAtPrice = parseInt(omnibusContainer.dataset.variantCompareAtPrice);
  const variantPrice = parseInt(omnibusContainer.dataset.variantPrice);

  // Check if a third‑party discount app injected a discounted price node.
  // This is for Discount Depot.
  const hasDiscountPriceApp = () => !!priceWrapper.querySelector('.axt_discounted_price');

  // Main logic to decide if Omnibus should be visible or hidden.
  const showOrHideOmnibus = (discountPriceAppPresent) => {
    getOmnibus({
      sku: omnibusContainer.dataset.sku,
      currency: omnibusContainer.dataset.currency,
      callbacks: {
        afterFetch: (omnibus) => {
          if (omnibus?.price) {
            // Math.round, not parseInt: 77.35 * 100 === 7734.999999999999,
            // which parseInt would truncate to 77.34.
            const omnibusPrice = Math.round(omnibus.price * 100);

            const hasNativeDiscount = variantCompareAtPrice > variantPrice;

            if (discountPriceAppPresent || hasNativeDiscount) {
              omnibusValue.innerText = formatPrice({ value: omnibusPrice });
              omnibusContainer.classList.remove('hidden');
            } else {
              omnibusContainer.classList.add('hidden');
            }
          } else {
            omnibusContainer.classList.add('hidden');
          }
        },
        errorCallback: () => {
          omnibusContainer.classList.add('hidden');
        },
      },
    });
  };

  // 1) Call getOmnibus immediately, even if no external discount app is present.
  const initialDiscountPriceApp = hasDiscountPriceApp();
  showOrHideOmnibus(initialDiscountPriceApp);

  // 2) Set up MutationObserver only to react to discounts injected later by external apps.
  const observer = new MutationObserver((mutations, obs) => {
    const discountPriceApp = hasDiscountPriceApp();
    if (discountPriceApp) {
      showOrHideOmnibus(discountPriceApp);
      obs.disconnect();
    }
  });

  observer.observe(container, { childList: true, subtree: true });

  // 3) Safety timeout: stop observing after a while to avoid leaking observers.
  setTimeout(() => {
    observer.disconnect();
  }, 5000);
};

/**
 * Reads a formatted money string back into cents.
 * The values injected by Discount Depot are only available as rendered text, so
 * the separators are resolved by position: a final group of exactly two digits
 * behind the last `.` or `,` is the decimal part, anything else is thousands.
 *
 * @param {string} text - A rendered price, e.g. "€ 1.234,56".
 * @returns {number|null} The value in cents, or null when nothing parses.
 */
const parsePriceToCents = (text) => {
  const cleaned = String(text ?? '').replace(/[^\d.,]/g, '');
  if (!cleaned) return null;

  const lastSeparator = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
  const hasDecimals = lastSeparator !== -1 && cleaned.length - lastSeparator === 3;

  const units = (hasDecimals ? cleaned.slice(0, lastSeparator) : cleaned).replace(/\D/g, '');
  if (!units) return null;

  const decimals = hasDecimals ? cleaned.slice(lastSeparator + 1) : '00';
  return parseInt(units, 10) * 100 + parseInt(decimals, 10);
};

/**
 * Brings a Discount Depot discount in line with the theme's own presentation.
 *
 * Three things the app gets wrong on its own: it prints the original price ahead
 * of the discounted one, it formats with its own separators instead of the
 * shop's money format, and it carries no compare-at price, so the percentage
 * badge — fed from Liquid and from `updateProductPrice` on the native path —
 * stays empty. A no-op when the app is absent, so the native path keeps owning
 * the badge, and idempotent, so it can run on every re-render the app performs.
 *
 * @param {HTMLElement} scope - Element holding both the price node and the badge.
 * @param {Object} [options]
 * @param {number} [options.price] - List price in cents of a newly selected
 *   variant. The app does not recompute its own pair on a variant change, so the
 *   discounted value is rescaled with the ratio last seen from the app.
 */
export const updateDiscountAppPrices = (scope, { price } = {}) => {
  if (!scope) return;

  const priceNode = scope.querySelector('[data-price]');
  if (!priceNode) return;

  const originalNode = priceNode.querySelector('.axt_show_original');
  const discountedNode = priceNode.querySelector('.axt_discounted_price');
  if (!originalNode || !discountedNode) return;

  const renderedOriginal = parsePriceToCents(originalNode.innerText);
  const renderedDiscounted = parsePriceToCents(discountedNode.innerText);
  const ratio = parseFloat(priceNode.dataset.discountAppRatio);

  let original = renderedOriginal;
  let discounted = renderedDiscounted;

  if (price && Number.isFinite(ratio)) {
    original = price;
    discounted = Math.round(price * ratio);
  }

  if (!original || !discounted || discounted >= original) return;

  // Remember the app's own discount, but only from a pair the app rendered
  // itself: a rescaled pair would just echo the ratio back.
  if (!price) priceNode.dataset.discountAppRatio = String(discounted / original);

  const originalContent = formatPrice({ value: original });
  const discountedContent = formatPrice({ value: discounted });
  if (originalContent && originalNode.innerText !== originalContent) originalNode.innerText = originalContent;
  if (discountedContent && discountedNode.innerText !== discountedContent) discountedNode.innerText = discountedContent;

  // The app prints the original first, the theme leads with the sale price.
  // Reordered here rather than with flex `order`, which would turn the node into
  // a flex box and move the baseline the badge row aligns on.
  if (discountedNode.nextElementSibling !== originalNode) {
    priceNode.insertBefore(discountedNode, originalNode);
  }

  const badge = scope.querySelector('[data-price-percentage]');
  if (!badge) return;

  const percentage = `-${Math.round(((original - discounted) * 100) / original)}%`;
  if (badge.innerText !== percentage) badge.innerText = percentage;
  badge.style.display = '';
};

/**
 * Propagates a Discount Depot discount into markup the app never sees.
 *
 * The quick-buy modal is fetched after load, and the app scans the DOM once at
 * startup: `window.AXT_OBJ` carries configuration only, with no entry point to
 * ask for a re-scan. Its price would therefore stay undiscounted while the tile
 * the customer just clicked shows the discount.
 *
 * The discount is not invented here. It is read back from a node for the same
 * product that the app decorated itself, and nothing happens when there is none,
 * so the modal can only ever repeat what the page already states. The limit is
 * the same the tile already has: one product-level discount, not a per-variant one.
 *
 * @param {HTMLElement} scope - Element holding the price node.
 */
export const seedDiscountAppPrices = (scope) => {
  if (!scope) return;

  const priceNode = scope.querySelector('[data-price]');
  if (!priceNode || priceNode.querySelector('.axt_discounted_price')) return;

  const productId = priceNode.id;
  if (!productId) return;

  const source = document.querySelector(`[data-discount-app-ratio].axt-cp-custom.${CSS.escape(productId)}`);
  const ratio = parseFloat(source?.dataset?.discountAppRatio);
  if (!Number.isFinite(ratio) || ratio <= 0 || ratio >= 1) return;

  const original = parsePriceToCents(priceNode.innerText);
  if (!original) return;

  const discountedNode = document.createElement('span');
  discountedNode.className = 'axt_discounted_price';
  discountedNode.innerText = formatPrice({ value: Math.round(original * ratio) }) ?? '';

  const originalNode = document.createElement('span');
  originalNode.className = 'axt_show_original';
  originalNode.innerText = formatPrice({ value: original }) ?? '';

  priceNode.replaceChildren(discountedNode, originalNode);
  priceNode.dataset.discountAppRatio = String(ratio);
};

const discountAppQueue = new Set();
let discountAppFlushTimer = null;

/**
 * Writes one Discount Depot rule into a price node with the same markup the app
 * renders, so `initDiscountAppPricing` takes over from there exactly as it does
 * for the nodes the app decorated itself. Built as nodes rather than from the
 * app's HTML strings: its rule label and inline colour are hidden or overridden
 * by the theme anyway.
 *
 * @param {HTMLElement} priceNode - The `axt-cp-custom` price node.
 * @param {Object} rule - The entry the endpoint returns for the node's variant.
 */
const renderDiscountAppPrice = (priceNode, rule) => {
  const { variantPrice, variantDiscountedPrice, compareAtPrice } = rule.price;
  const toCents = (value) => Math.round(Number(value) * 100);

  const discountedNode = document.createElement('span');
  discountedNode.className = 'axt_discounted_price';
  discountedNode.innerText = formatPrice({ value: toCents(variantDiscountedPrice) }) ?? '';

  // Same rules as the app: no struck price without `show_wasprice_at`, and the
  // compare-at price takes its place only when the rule asks for it.
  if (rule.show_wasprice_at == null || rule.show_wasprice_at === 'null') {
    priceNode.replaceChildren(discountedNode);
    return;
  }

  const showCompareAt = rule.show_compare_at_price === 1 && compareAtPrice != null && compareAtPrice > variantPrice;

  const originalNode = document.createElement('span');
  originalNode.className = 'axt_show_original';
  originalNode.innerText = formatPrice({ value: toCents(showCompareAt ? compareAtPrice : variantPrice) }) ?? '';

  priceNode.replaceChildren(originalNode, ' ', discountedNode);
};

/**
 * Sends the queued price nodes to Discount Depot's pricing endpoint in a single
 * request, with the same payload the app builds from `window.AXT_OBJ`, so the
 * customer group, market and currency are the ones the app would apply.
 */
const flushDiscountAppPrices = async () => {
  discountAppFlushTimer = null;

  const priceNodes = Array.from(discountAppQueue).filter((node) => node.isConnected);
  discountAppQueue.clear();

  const config = window.AXT_OBJ;
  if (!priceNodes.length || !config?.axt_host) return;

  const variantIDs = [...new Set(priceNodes.map((node) => node.getAttribute('axt-cp-variant-id')))].join(',');
  const productIDs = [...new Set(priceNodes.map((node) => node.id).filter(Boolean))].join(',');
  if (!variantIDs || !productIDs) return;

  const customerId = window.__st?.cid;
  const customerTags = config.axt_customer_tags?.length
    ? config.axt_customer_tags.map((tag) => `'${tag.trim()}'`).join(',')
    : "'--'";

  let rules;
  try {
    const response = await fetch(`${config.axt_host}/applyPriceShippingRules`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        shop: config.axt_shop,
        variantIDs,
        productIDs,
        countryCode: config.axt_customer_country,
        userCurrency: config.axt_user_currency,
        baseCurrency: config.axt_base_currency,
        flag: '',
        quantities: '',
        loginCustomerId: customerId,
        customerLoginStatus: !!customerId,
        currencyRate: window.Shopify?.currency?.rate,
        currency: window.Shopify?.currency?.active,
        locale: window.Shopify?.locale,
        country: config.axt_customer_country,
        customerTags,
        token: '',
      }),
    });
    if (!response.ok) return;

    // An array holding a map keyed by variant id when a rule applies,
    // `{ status: false }` when none does.
    const data = await response.json();
    rules = Array.isArray(data) ? data[0] : null;
  } catch (e) {
    console.warn('Discount Depot: pricing request failed', e);
    return;
  }

  if (!rules) return;

  priceNodes.forEach((priceNode) => {
    const rule = rules[priceNode.getAttribute('axt-cp-variant-id')];
    if (!rule?.price || priceNode.querySelector('.axt_discounted_price')) return;
    renderDiscountAppPrice(priceNode, rule);
  });
};

/**
 * Asks Discount Depot for the prices of markup inserted after load.
 *
 * The app requests its discounts once, for the price nodes in the page when it
 * starts, and exposes no way to ask again: tiles appended by "view more", a
 * filter change or a section fetched later would keep the full price. Nodes
 * queued in the same task go out as one request, so a whole page of tiles costs
 * a single call, like the app's own.
 *
 * @param {HTMLElement} scope - Element holding the price node(s).
 */
export const requestDiscountAppPrices = (scope) => {
  if (!scope || !window.AXT_OBJ?.axt_host) return;

  scope.querySelectorAll('[data-price].axt-cp-custom[axt-cp-variant-id]').forEach((priceNode) => {
    if (!priceNode.querySelector('.axt_discounted_price')) discountAppQueue.add(priceNode);
  });

  if (discountAppQueue.size && !discountAppFlushTimer) {
    discountAppFlushTimer = setTimeout(flushDiscountAppPrices, 0);
  }
};

/**
 * Keeps the theme's presentation applied to whatever Discount Depot renders.
 *
 * The app rewrites its own markup more than once — on load and again on a
 * variant change — so the observer stays connected for the life of the page
 * instead of stopping at the first injection, which left later renders with the
 * app's order, separators and an unrecomputed badge. It is detached around our
 * own writes so they cannot feed back into it.
 *
 * @param {HTMLElement} scope - Element holding both the price node and the badge.
 */
export const initDiscountAppPricing = (scope) => {
  if (!scope) return;

  // Watch the price node alone, not the whole scope: a collection page holds
  // dozens of these, and everything the app rewrites lives inside it.
  const priceNode = scope.querySelector('[data-price]');
  if (!priceNode) return;

  const observer = new MutationObserver(() => sync());

  const watch = () => observer.observe(priceNode, { childList: true, subtree: true, characterData: true });

  const sync = (options) => {
    observer.disconnect();
    updateDiscountAppPrices(scope, options);
    watch();
  };

  sync();

  return sync;
};

/**
 * Retrieves the A11y translations from the window object.
 * @returns {Object} The A11y translations.
 */
export const getA11yTranslations = () => {
  if (!window.dAgency?.translations?.slider) return {};

  return {
    a11y: {
      prevSlideMessage: window.dAgency.translations.slider.prev_slide,
      nextSlideMessage: window.dAgency.translations.slider.next_slide,
      firstSlideMessage: window.dAgency.translations.slider.first_slide,
      lastSlideMessage: window.dAgency.translations.slider.last_slide,
      paginationBulletMessage: window.dAgency.translations.slider.pagination_bullet_message,
    },
  };
};

/**
 * Locks page scroll and blocks interaction outside a given wrapper.
 * Applies `inert` to all elements outside the wrapper, keeping the wrapper fully interactive.
 *
 * @param {boolean} enable - If true, disables page scroll and makes background inert; if false, restores normal behavior.
 * @param {HTMLElement} wrapper - The element to keep interactive while everything else is inert.
 */
export const lockPageInteraction = (enable, wrapper) => {
  if (!wrapper) return;

  // Enable or disable page scrolling
  document.documentElement.style.overflow = enable ? 'hidden' : '';

  Array.from(document.body.children).forEach((el) => {
    if (!enable) {
      // Restore the original inert state if it was modified by this function
      if (el.dataset.originalInert !== undefined) {
        el.inert = el.dataset.originalInert === 'true';
        delete el.dataset.originalInert;
      }
    } else {
      // Save the original inert state if not already saved
      if (el.dataset.originalInert === undefined) {
        el.dataset.originalInert = el.inert.toString();
      }
      // Apply inert to all elements except the wrapper and its ancestors
      el.inert = !el.contains(wrapper);
    }
  });
};

/**
 * Lets a mouse drag scroll a horizontally overflowing row, the way touch already
 * does natively. Rows like these hide their scrollbar, so without it a desktop
 * mouse has no way to reach the items past the visible edge.
 * A drag past a few pixels scrolls the row and swallows the click that ends it,
 * so the links inside still work on a plain click.
 *
 * @param {HTMLElement} el - The scrollable element (`overflow-x: auto`).
 */
export const enableDragScroll = (el) => {
  if (!el || el.dataset.dragScroll) return;
  el.dataset.dragScroll = 'true';

  const threshold = 5;
  let startX = 0;
  let startScrollLeft = 0;
  let dragged = false;

  const onMove = (e) => {
    const deltaX = e.clientX - startX;
    if (!dragged && Math.abs(deltaX) < threshold) return;

    if (!dragged) {
      dragged = true;
      el.classList.add('is-dragging');
      window.getSelection()?.removeAllRanges();
    }
    el.scrollLeft = startScrollLeft - deltaX;
  };

  const onEnd = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onEnd);
    window.removeEventListener('pointercancel', onEnd);
    el.classList.remove('is-dragging');
  };

  el.addEventListener('pointerdown', (e) => {
    // Touch and pen scroll natively; only a primary mouse button drags.
    if (e.pointerType !== 'mouse' || e.button !== 0 || el.scrollWidth <= el.clientWidth) return;

    startX = e.clientX;
    startScrollLeft = el.scrollLeft;
    dragged = false;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onEnd);
    window.addEventListener('pointercancel', onEnd);
  });

  // The click that ends a drag must not follow the link under the pointer.
  el.addEventListener(
    'click',
    (e) => {
      if (!dragged) return;
      e.preventDefault();
      e.stopPropagation();
      dragged = false;
    },
    true,
  );

  // Links and images are natively draggable, which would take over the gesture.
  el.addEventListener('dragstart', (e) => e.preventDefault());
};
